import { ReasoningLimitBanner } from '@/components/reasoning/ReasoningLimitBanner';
import type { ReasoningStepData } from '@/components/reasoning/ReasoningStepsList';
import { ThinkingIndicator } from '@/components/reasoning/ThinkingIndicator';

import type { ChainFinalAnswerProps } from './ChainFinalAnswer';
import { ChainFinalAnswer } from './ChainFinalAnswer';
import { ChainFailedNotice, ChainMaxStepsNotice } from './ChainNotices';
import type { ChainResponseState } from './chainResponseState';
import { assertNever, resolveChainResponseState } from './chainResponseState';
import { UserMessageBubble } from './UserMessageBubble';

export interface ChainTurnProps extends Omit<
  ChainFinalAnswerProps,
  'finalStep'
> {
  isPolling: boolean;
  progressStatus: string;
  partialSteps: ReasoningStepData[];
}

/** One exchange in the thread: the user's message and the assistant's response. */
export function ChainTurn({
  isPolling,
  progressStatus,
  partialSteps,
  ...responseProps
}: ChainTurnProps) {
  const { chain, orgId, isLastChain, regeneratingChainId } = responseProps;
  const state = resolveChainResponseState({
    chain,
    isLastChain,
    isPolling,
    isRegenerating: regeneratingChainId === chain.id,
  });

  return (
    <div className='space-y-4'>
      <UserMessageBubble
        text={chain.user_message}
        orgId={orgId}
        files={chain.files}
      />

      {/* `min-w-0` on BOTH levels is load-bearing: a flex child defaults to
          `min-width:auto`, so anything wide in the agent's message — a code
          block, a table, a proposal card — widened this row past the viewport
          and cut the agent's own sentences off mid-word on a phone.
          `max-w-full` is the belt to those braces. */}
      <div className='flex gap-2 sm:gap-3 items-start mb-6 min-w-0 max-w-full'>
        <div className='flex-1 min-w-0 max-w-full space-y-3'>
          <ChainResponse
            state={state}
            progressStatus={progressStatus}
            partialSteps={partialSteps}
            {...responseProps}
          />
        </div>
      </div>
    </div>
  );
}

interface ChainResponseProps extends Omit<ChainFinalAnswerProps, 'finalStep'> {
  state: ChainResponseState;
  progressStatus: string;
  partialSteps: ReasoningStepData[];
}

function ChainResponse({
  state,
  progressStatus,
  partialSteps,
  ...props
}: ChainResponseProps) {
  const { chain, isLastChain, regeneratingChainId } = props;
  const retryProps = {
    chainId: chain.id,
    canRetry: isLastChain,
    isRetryDisabled: Boolean(regeneratingChainId),
    isRegenerating: regeneratingChainId === chain.id,
    onRetry: props.onRetry,
  };

  switch (state.status) {
    case 'answered':
      return <ChainFinalAnswer finalStep={state.finalStep} {...props} />;
    case 'thinking':
      return (
        <ThinkingIndicator
          progressStatus={progressStatus}
          partialSteps={partialSteps}
          chainId={chain.id}
          onOpenDynamicViewSidebar={props.onOpenDynamicViewSidebar}
        />
      );
    case 'max-steps':
      return (
        <ChainMaxStepsNotice
          {...retryProps}
          onOpenReasoningSidebar={props.onOpenReasoningSidebar}
        />
      );
    case 'budget-exceeded':
      return <ReasoningLimitBanner />;
    case 'failed':
      return <ChainFailedNotice {...retryProps} />;
    case 'empty':
      return null;
    default:
      return assertNever(state);
  }
}
